import { type TransportNocTripRow, formatFlightDesignator } from "@seagames/contracts";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { formatClockIn, formatDateTime, formatDayShort, wallTimeIn } from "../../lib/format/datetime";
import { minutesBetween } from "../../lib/transport/view";
import { ChevronLeftIcon, ChevronRightIcon } from "../transport/icons";
import { useDuration } from "../transport/parts";

/**
 * Pieces of the NOC and provider trip lists (her `.l3trip2` card): the time
 * column, the route line, the "Follows MH 803" note, a day stepper. Times are
 * the games' clock with the viewer's own in the tooltip (PLAN X18).
 */

/** Her time column: "11:45AM" big, the day under it. */
export function TimeCell({ at, timeZone, muted }: { at: string; timeZone: string; muted?: boolean }) {
	const wall = wallTimeIn(at, timeZone);
	return (
		<span className="grid content-start" title={formatDateTime(at)}>
			<b className={`font-display text-[1.125rem] leading-[1.15] font-bold tabular-nums ${muted ? "text-gms-ink-2" : "text-gms-navy-ink"}`}>{formatClockIn(at, timeZone)}</b>
			<small className="text-[0.75rem] leading-snug text-gms-ink-3">{wall ? formatDayShort(wall.date) : "—"}</small>
		</span>
	);
}

/** "KLIA T1 → Bukit Jalil Games Village". */
export function RouteLine({ from, to }: { from: string; to: string }) {
	const { t } = useTranslation("transportFleet");
	return <b className="text-[0.875rem] leading-snug font-semibold text-gms-navy-ink">{t("trips.route", { from, to })}</b>;
}

/**
 * What a transfer's pickup follows, worded as S4's trip card does (same
 * `transport:trip.*` keys): "Follows TG 415", "Follows Coach SG-1 (+2 h)",
 * "Fixed time" only when `follows_travel` is false; null for a venue trip.
 * `flights` and `services` (a coach, a ferry, a typed flight) are the
 * viewer's OWN legs' — the API filters them for a NOC (PLAN §0b Q3).
 */
export function useFollowLabel(): (trip: Pick<TransportNocTripRow, "source" | "follows_travel" | "flights" | "services">) => string | null {
	const { t } = useTranslation("transport");
	const duration = useDuration();
	return (trip) => {
		if (trip.source === "ADHOC") return null;
		if (!trip.follows_travel) return t("trip.fixedTime");
		const followed = [
			...trip.flights.map((f) => ({ name: formatFlightDesignator(f.carrier_code, f.flight_number), scheduled: f.scheduled_at, expected: f.expected_at })),
			...(trip.services ?? []).map((x) => ({ name: x.service_no, scheduled: x.scheduled_at, expected: x.expected_at })),
		];
		if (followed.length === 0) return t("trip.followsTravel");
		const names = followed.map((x) => x.name).join(", ");
		// The largest move among them (a transfer waits for its latest arrival).
		const moved = followed.reduce<number | null>((m, x) => {
			const d = minutesBetween(x.scheduled, x.expected);
			return d !== null && d !== 0 && (m === null || Math.abs(d) > Math.abs(m)) ? d : m;
		}, null);
		return moved ? t(moved > 0 ? "trip.followsLate" : "trip.followsEarly", { service: names, delay: duration(moved) }) : t("trip.follows", { service: names });
	};
}

/** A meta line of a trip card: items split by a quiet middle dot. */
export function MetaLine({ children }: { children: ReactNode[] }) {
	const items = children.filter((c) => c !== null && c !== undefined && c !== false);
	return (
		<span className="flex flex-wrap items-center gap-x-[0.375rem] gap-y-[0.125rem] text-[0.78125rem] text-gms-ink-3">
			{items.map((c, i) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: static, ordered meta items
				<span key={i} className="inline-flex items-center gap-x-[0.375rem]">
					{i > 0 && <span aria-hidden>·</span>}
					{c}
				</span>
			))}
		</span>
	);
}

/** ‹ Tue 14 Oct › with "Today" — the provider's day. */
export function DayStepper({ date, today, onDate }: { date: string; today: string; onDate: (ymd: string) => void }) {
	const { t } = useTranslation("transportFleet");
	const step = "grid size-[2.25rem] cursor-pointer place-items-center rounded-[0.5rem] border border-gms-line-2 bg-base-100 text-gms-navy-ink transition-colors hover:border-gms-navy [&>svg]:size-[1rem]";
	const shift = (n: number) => new Date(Date.parse(`${date}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
	return (
		<div className="flex flex-wrap items-center gap-[0.375rem]">
			<button type="button" className={step} aria-label={t("trips.previousDay")} title={t("trips.previousDay")} onClick={() => onDate(shift(-1))}>
				<ChevronLeftIcon />
			</button>
			<label className="relative inline-flex h-[2.25rem] min-w-[9.5rem] cursor-pointer items-center justify-center gap-[0.375rem] rounded-[0.5rem] border border-gms-line-2 bg-base-100 px-[0.75rem] text-[0.875rem] font-semibold text-gms-navy-ink hover:border-gms-navy">
				{formatDayShort(date)}
				{date === today && <span className="text-[0.75rem] font-medium text-gms-ink-3">{t("trips.today")}</span>}
				<input
					type="date"
					value={date}
					onChange={(e) => e.target.value && onDate(e.target.value)}
					aria-label={t("trips.pickDay")}
					className="absolute inset-0 cursor-pointer opacity-0"
				/>
			</label>
			<button type="button" className={step} aria-label={t("trips.nextDay")} title={t("trips.nextDay")} onClick={() => onDate(shift(1))}>
				<ChevronRightIcon />
			</button>
			{date !== today && (
				<button type="button" className="btn btn-line btn-sm" onClick={() => onDate(today)}>
					{t("trips.backToToday")}
				</button>
			)}
		</div>
	);
}
